import _ from 'underscore';
import { useContext, useMemo, useEffect, useCallback, useState } from 'react';

import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { usePostAssetEvent, AssetChannel, AssetScenario } from '@bingads-webui-react/copilot-event-provider';
import { useEventLog } from '@bingads-webui-campaign-react/copilot-common';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { LoggingContext } from './logging-context';

// Initialize the postAssetEventLog function in the LoggingContext.
// It only needs to be called once.
export const useInitCopilotLoggers = ({
  campaignId,
  feature = 'DisplayPlusCreative',
  isEditScenario = false,
}) => {
  const serverContext = useContext(ServerContext);
  const activity = useContext(ActivityContext);
  const [, updateLoggingContext] = useContext(LoggingContext);

  const logTrace = useMemo(() => _.get(activity, 'trace'), [activity]);

  const { Permissions, DynamicPermissions } = serverContext || { };
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const [isRegionRestricted, setIsRegionRestricted] = useState(true);
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Recommendations',
      permissions,
      serverContext,
    }).then(setIsRegionRestricted);
  }, [permissions, serverContext]);

  const postAssetEventLog = usePostAssetEvent({
    serverContext,
    channel: AssetChannel.WEB_UI,
    feature,
    campaignId,
    scenario: isEditScenario ? AssetScenario.EDIT_WORK_FLOW: AssetScenario.CREATE_WORK_FLOW,
    shouldLog: _.isFunction(logTrace) && !isRegionRestricted,
    logTrace,
  });

  useEffect(() => {
    updateLoggingContext({ postAssetEventLog });
  }, [postAssetEventLog, updateLoggingContext]);
};

export const useCopilotLoggers = () => {
  const [loggingContext] = useContext(LoggingContext);
  const { postAssetEventLog = _.noop, copilotTracking } = loggingContext || {};

  const {
    logEnterFlow,
    logBannerGenerated,
    logSuggestionsViewed,
    logSuggestionsApplied,
  } = useEventLog(postAssetEventLog);

  const logSuggestionsViewedWrapped = useCallback((imageSuggestionIds, videoSuggestionIds) => {
    if (!copilotTracking) { return; }

    const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(imageSuggestionIds);
    const viewedVideoSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(videoSuggestionIds);

    if (viewedImageSuggestionIds.length > 0 || viewedVideoSuggestionIds.length > 0) {
      logSuggestionsViewed(undefined /* textSuggestions */, viewedImageSuggestionIds, viewedVideoSuggestionIds);

      copilotTracking.trackViewedSuggestionIds(viewedImageSuggestionIds);
    }
  }, [copilotTracking, logSuggestionsViewed]);

  const logSuggestionsAppliedWrapped = useCallback((
    campaignId,
    adgroupId,
    textSuggestionIds,
    imageSuggestionIds,
    videoSuggestionIds,
    appliedVideoAssetIds
  ) => {
    logSuggestionsApplied(
      campaignId,
      adgroupId,
      textSuggestionIds,
      imageSuggestionIds,
      videoSuggestionIds,
      appliedVideoAssetIds
    );
  }, [logSuggestionsApplied]);

  return {
    logEnterFlow,
    logBannerGenerated,
    logSuggestionsViewed: logSuggestionsViewedWrapped,
    logSuggestionsApplied: logSuggestionsAppliedWrapped,
  };
};
